import * as CL from '@design-system/component-library';
import { useImperativeHandle, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { OfferSubCategory } from '../../../common/enums.js';
import { productInformationMsg, t } from '../../../common/i18n';
import { toDacInstallationDate } from '../../../common/installationDatesApiCoCo.js';
import { findBasketItemByCartItemId, getCheckedAddonKey } from '../../ShoppingBasket/shoppingBasketHelpersCoCo.js';
import { useCheckoutContext } from '../context/CheckoutContextCoCo.js';
import { usePhoneNumbersCoCo } from '../hooks/usePhoneNumbersCoCo.js';
import { shouldShowInstallationDateSelection } from '../productConfig/productConfigRegistryCoCo.js';
import { InstallationDateSelectionCoCo } from '../productConfig/shared/InstallationDateSelectionCoCo.js';
import { syncAllContactsToParentForm } from '../utils/contactOrPurposeUtils.js';
import { getBasketItemAvailability } from '../utils/placeOrderUtilsCoCo.js';
import { isProductSubcategory } from '../utils/productSubcategoryUtilsCoCo.js';
import { CheckoutFooterCoCo } from './common/CheckoutFooterCoCo.js';
import { OrderItemUnitsCoCo } from './products/OrderItemUnitsCoCo.js';
import { ProductEntryCoCo } from './products/ProductEntryCoCo.js';
import type { ShoppingBasketType } from '../../../common/types/shoppingBasketCoCo.js';
import type { CustomerResponse } from '../../../generated/api/customerResponse.js';
import type { ContactsUpdateMethods } from '../placeOrderTypes.js';
import type {
  CheckoutStepValidationHandleCoCo,
  PlaceOrderFormDataCoCo,
  PlaceOrderItemUnitCoCo,
} from '../placeOrderTypesCoCo.js';
import type {
  ConfigHandle,
  InstallationContactHandle,
  InstallationDateSelectionProps,
} from '../productConfig/productConfigTypesCoCo.js';
import type { ContactOrPurposeHandle } from './products/PlaceOrderContactOrPurpose/PlaceOrderContactOrPurpose.js';
import type { JSX, Ref } from 'react';

import './ProductsStepCoCo.scss';

export interface ProductsStepProps {
  onNext: () => void | Promise<void>;
  onPrevious: () => void;
  contactsUpdateMethods: ContactsUpdateMethods;
  orderSummary: JSX.Element;
  installationDateSelection: InstallationDateSelectionProps;
  validationRef?: Ref<CheckoutStepValidationHandleCoCo>;
  customerInfo?: CustomerResponse;
}

export const getInstallationAvailabilityGroupKey = (
  groupedOrderItemUnits: Record<string, PlaceOrderItemUnitCoCo[]>,
  basket: ShoppingBasketType
): string | undefined => {
  const installationAvailabilityGroup = Object.entries(groupedOrderItemUnits).find(([, items]) => {
    const basketItem = findBasketItemByCartItemId(basket.productOfferings, items[0]?.basketItemId);
    return basketItem ? shouldShowInstallationDateSelection(basketItem) : false;
  });
  return installationAvailabilityGroup ? installationAvailabilityGroup[0] : undefined;
};

export const ProductsStepCoCo = ({
  onNext,
  onPrevious,
  orderSummary,
  contactsUpdateMethods,
  installationDateSelection,
  validationRef,
  customerInfo,
}: ProductsStepProps) => {
  const { basket, mdmId, shoppingBasketUpdateMethods, selectedProductOffers } = useCheckoutContext();
  const methods = useFormContext<PlaceOrderFormDataCoCo>();
  const [installationValidationMessage, setInstallationValidationMessage] = useState<string>();
  const watchedOrderItemUnits = methods.watch('products.placeOrderItemUnits') || [];
  // Refs for imperative validation — keyed by `${basketItemId}-${basketItemIndex}`
  const contactFormRefs = useRef<Record<string, ContactOrPurposeHandle | null>>({});
  const installationContactRefs = useRef<Record<string, InstallationContactHandle | null>>({});
  const configRefs = useRef<Record<string, ConfigHandle | null>>({});
  const hasInstallationAvailabilityProduct = basket.productOfferings.some(shouldShowInstallationDateSelection);

  const { phoneNumbers, phoneNumbersLoading, onLoadMoreNumbers } = usePhoneNumbersCoCo({
    selectedProductOffers,
    mdmId,
  });

  const validateOrderItemUnit = async (unit: PlaceOrderItemUnitCoCo): Promise<boolean> => {
    const basketItem = findBasketItemByCartItemId(basket.productOfferings, unit.basketItemId);
    if (!basketItem) {
      return true;
    }

    const refId = `${unit.basketItemId}-${unit.basketItemIndex}`;
    const contactRef = contactFormRefs.current[refId];
    if (contactRef && !(await contactRef.validate())) {
      contactRef.scrollToContactError(refId);
      return false;
    }

    const installationContactRef = installationContactRefs.current[refId];
    if (installationContactRef && !(await installationContactRef.validate())) {
      installationContactRef.scrollToFirstError();
      return false;
    }

    const configRef = configRefs.current[refId];
    if (configRef && !(await configRef.validate())) {
      configRef.scrollToFirstError();
      return false;
    }

    return true;
  };

  const validateOrderItemUnits = async (): Promise<boolean> => {
    for (const unit of watchedOrderItemUnits) {
      if (!(await validateOrderItemUnit(unit))) {
        return false;
      }
    }
    return true;
  };

  const validateInstallationAvailability = async (): Promise<boolean> => {
    const confirmedTimeSlotIds = methods.getValues('products.installationAppointmentTimeSlotIds') ?? {};
    if (!hasInstallationAvailabilityProduct || Object.keys(confirmedTimeSlotIds).length > 0) {
      return true;
    }

    const preferredInstallationDate = methods.getValues('products.preferredInstallationDate');
    if (!preferredInstallationDate) {
      setInstallationValidationMessage(t.USTU('Please select an installation date before continuing.'));
      return false;
    }

    const dacInstallationDate = toDacInstallationDate(preferredInstallationDate);
    if (installationDateSelection.availabilityCheckedDate !== dacInstallationDate) {
      const availabilityChecked = await installationDateSelection.onCheckAvailability?.();
      setInstallationValidationMessage(
        availabilityChecked === false
          ? t.Z0MM('Installation availability could not be checked. Please try again.')
          : t.VNNL('Please confirm an available installation date before continuing.')
      );
      return false;
    }

    setInstallationValidationMessage(t.VNNL('Please confirm an available installation date before continuing.'));
    return false;
  };

  const validateProductsStep = async (): Promise<boolean> => {
    if (!(await validateOrderItemUnits())) {
      return false;
    }

    // Push every contact / purpose-of-use form into the main form, including the optional ones
    // that step 1 skipped, so the user information ends up in the product order request.
    await syncAllContactsToParentForm(contactFormRefs);

    if (!(await validateInstallationAvailability())) {
      return false;
    }

    setInstallationValidationMessage(undefined);
    return true;
  };

  useImperativeHandle(validationRef, () => ({ validate: validateProductsStep }));

  const handleNext = async () => {
    if (!(await validateProductsStep())) {
      return;
    }
    await onNext();
  };

  // Going back to the Customer step should not retain the directory-listing (number publicity)
  // selection — the next time the user reaches Products again it must fall back to the
  // catalogue default ("Public"), not whatever was previously chosen. Products step's "Previous"
  // always targets the Customer step (see STEP_ORDER), so this reset is safe to do
  // unconditionally here without affecting Products <-> Delivery navigation.
  const handlePrevious = () => {
    const currentUnits = methods.getValues('products.placeOrderItemUnits') ?? [];
    const resetUnits = currentUnits.map(unit => ({
      ...unit,
      directoryListingPublicity: undefined,
      directoryListingPublicityDetails: undefined,
    }));
    methods.setValue('products.placeOrderItemUnits', resetUnits, {
      shouldDirty: false,
      shouldTouch: false,
      shouldValidate: false,
    });
    onPrevious();
  };

  const confirmInstallationTimeSlots = async (timeSlotIds: Record<string, string>) => {
    const confirmed = await installationDateSelection.onConfirmInstallationTimeSlots?.(timeSlotIds);
    if (confirmed) {
      setInstallationValidationMessage(undefined);
    }
    return confirmed ?? false;
  };

  // Group placeOrderItemUnits by productId|addonKey so that multiple entries for the same
  // product + same addon selection collapse into a single row.
  const groupedByProductAndAddon = watchedOrderItemUnits.reduce<Record<string, typeof watchedOrderItemUnits>>(
    (acc, unit) => {
      const basketEntry = findBasketItemByCartItemId(basket.productOfferings, unit.basketItemId);
      if (!basketEntry) {
        return acc;
      }
      const key = `${basketEntry.id}|${getCheckedAddonKey(basketEntry)}`;
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(unit);
      return acc;
    },
    {}
  );
  const installationAvailabilityGroupKey = getInstallationAvailabilityGroupKey(groupedByProductAndAddon, basket);
  const shouldShowExistingSubscriptionTerminationNotice = basket.productOfferings.some(item =>
    isProductSubcategory(item.productOfferingType?.subcategory, OfferSubCategory.FIXED_BROADBAND)
  );

  const orderItemUnits = Object.entries(groupedByProductAndAddon).flatMap(([groupKey, items], index) => {
    const basketItem = findBasketItemByCartItemId(basket.productOfferings, items[0].basketItemId);
    if (!basketItem) {
      return [];
    }
    const rowKey = items.map(unit => unit.basketItemId).join('|');
    const groupItemsForProduct = items
      .map(item => findBasketItemByCartItemId(basket.productOfferings, item.basketItemId))
      .filter((item): item is NonNullable<typeof item> => item !== undefined);
    return (
      <li key={rowKey}>
        <div className="of-products-step--item-line">
          <div className="of-products-step--badge-container">
            <CL.Badge type="numeric" color="info" text={String(index + 1)} />
          </div>
          <ProductEntryCoCo
            basketItem={basketItem}
            groupItems={groupItemsForProduct}
            changeQuantity={shoppingBasketUpdateMethods.changeQuantity}
            availabilityClassName="ds-margin-top--1"
            availability={getBasketItemAvailability(selectedProductOffers, basketItem.id)}
          />
          <OrderItemUnitsCoCo
            orderItemUnits={items}
            basketItem={basketItem}
            contactFormRefs={contactFormRefs}
            installationContactRefs={installationContactRefs}
            configRefs={configRefs}
            contactsUpdateMethods={contactsUpdateMethods}
            phoneNumbers={phoneNumbers}
            phoneNumbersLoading={phoneNumbersLoading}
            onLoadMoreNumbers={onLoadMoreNumbers}
            customerInfo={customerInfo}
          />
          {hasInstallationAvailabilityProduct && groupKey === installationAvailabilityGroupKey && (
            <InstallationDateSelectionCoCo
              {...installationDateSelection}
              validationMessage={installationValidationMessage}
              showExistingSubscriptionTerminationNotice={shouldShowExistingSubscriptionTerminationNotice}
              onConfirmInstallationTimeSlots={confirmInstallationTimeSlots}
            />
          )}
        </div>
      </li>
    );
  });

  return (
    <div className="products-step">
      <h1>{t.R4EV(productInformationMsg)}</h1>
      <ul className="ds-shoppingcart-wrapper">{orderItemUnits}</ul>
      {orderSummary}
      <CheckoutFooterCoCo onNext={handleNext} onPrevious={handlePrevious} />
    </div>
  );
};
